/**
 * 2026-10-24 (card-panels-refetch) -- saves still in flight, so the CRM card
 * can hold Next and Close (X) until every save started on it has answered.
 *
 * Why: the queue card is now keyed by patient (pages/CRMQueue.jsx), so moving
 * on replaces it. A save that FAILED after the move would report its error on
 * a card nobody can see any more, and staff would think it saved.
 *
 * Counted at the transport: each tracked axios instance's adapter. The count
 * goes up only when a request is actually sent, and comes back down in
 * `finally`, however it ends: success, HTTP error, network error, abort or
 * cancel, or an adapter that throws. A request interceptor that throws before
 * sending never reaches the adapter, so it is never counted.
 *
 * Not counted (they run on their own, or are the click itself): reads
 * (GET/HEAD/OPTIONS), the queue lock traffic under /crm/queue/ (heartbeat,
 * acquire, release, next, takeover), Referral Intake's lock acquire/release,
 * and /auth/.
 */
import { useSyncExternalStore } from "react";
import axios from "axios";
import { api } from "../api/client";

const READS = new Set(["get", "head", "options"]);
const NOT_COUNTED = [
  /\/crm\/queue\//,
  /\/auth\//,
  /\/intake\/referrals\/[^/?]+\/(acquire|release)(\?|$)/,
];

let inFlight = 0;
const listeners = new Set();
const emit = () => listeners.forEach((listener) => listener());

export function isCountedWrite(config) {
  const method = String((config && config.method) || "get").toLowerCase();
  if (READS.has(method)) return false;
  const url = String((config && config.url) || "");
  return !NOT_COUNTED.some((re) => re.test(url));
}

export function writesInFlight() {
  return inFlight;
}

export function trackInstance(instance) {
  const original = instance && instance.defaults ? instance.defaults.adapter : null;
  if (!original || original.__cardWrites) return instance;
  const tracked = (config) => {
    const send = typeof original === "function" ? original : axios.getAdapter(original);
    const counted = isCountedWrite(config);
    if (counted) {
      inFlight += 1;
      emit();
    }
    let answer;
    try {
      answer = Promise.resolve(send(config));
    } catch (err) {
      answer = Promise.reject(err);
    }
    if (!counted) return answer;
    return answer.finally(() => {
      inFlight = Math.max(0, inFlight - 1);
      emit();
    });
  };
  tracked.__cardWrites = true;
  instance.defaults.adapter = tracked;
  return instance;
}

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

// True while any counted write is in flight.
export function useWritesInFlight() {
  return useSyncExternalStore(subscribe, writesInFlight, writesInFlight) > 0;
}

// `api` carries almost every card save; plain axios carries the ICD-10 save.
trackInstance(api);
trackInstance(axios);
